import React, {useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import {Field, Form, Formik} from 'formik';
import InputBox from '../../../../../components/formik/InputBox';
import {toast} from 'react-toastify';
import ActionButton from '../../../../../components/datatable/ActionButton';
import SimpleButtonGroup from '../../../../../components/formik/SimpleButtonGroup';
import skincareProDao from '../../../../../dao/skincareProDao';
import {useSelector} from 'react-redux';
import {getDetectionLanguages} from '../../../../../utils/skincareUtils';
import SelectBox from '../../../../../components/formik/SelectBox';
import smbUtils from '../../../../../utils/smbUtils';

const getCategoryType = pathname => {
  if (pathname.includes('list-product-category')) {
    return 'Skincare';
  } else if (pathname.includes('list-treatment-category')) {
    return 'Treatment';
  }
  return null;
};

export default function AddOrEditCategoryManagement({
  editData,
  handleCloseModel,
  showModal,
}) {
  const [langOptions, setLangOptions] = useState(getDetectionLanguages());
  const {token, customerId} = smbUtils.customerInfo(
    useSelector(state => state.components.auth.accountSummary)
  );
  const pathname = new URL(window.location.href).pathname;
  const categoryType = getCategoryType(pathname);
  const {t} = useTranslation();

  const getCategoryById = editData => {
    if (editData.id) {
      return {names: editData.names};
    }
    return {
      names: [{lang: 'en', isDefault: true, name: ''}],
    };
  };

  const addCategoryAPI = async categoryData => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('type', categoryType);
    tmp.append('names', JSON.stringify(categoryData.names));
    return await skincareProDao.createCategoryList(tmp);
  };
  const updateCategoryAPI = async categoryData => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('type', categoryType);
    tmp.append('categoryId', editData.id);
    tmp.append('names', JSON.stringify(categoryData.names));
    return await skincareProDao.updateCategoryList(tmp);
  };
  const onAdd = formikProps => {
    const newState = formikProps.values.names;

    const item = {
      lang: '',
      isDefault: false,
      name: '',
    };

    newState.push(item);
    formikProps.setFieldValue('names', newState);
  };
  const onRemove = (formikProps, index) => {
    const categoryData = formikProps.values.names;
    const isDefault = categoryData[index].isDefault;

    const newState = categoryData.filter((_, idx) => idx !== index);

    if (isDefault && newState.length > 0) {
      newState[0].isDefault = true;
    }

    formikProps.setFieldValue('names', newState);
  };
  const handleDefaultLanguage = (formik, index) => {
    const newState = formik.values.names.map((item, idx) => ({
      ...item,
      isDefault: idx === index,
    }));
    formik.setFieldValue('names', newState);
  };
  const isLangUnique = arr => {
    const langSet = new Set();
    return arr.every(item => {
      if (langSet.has(item.lang)) {
        return false;
      }
      langSet.add(item.lang);
      return true;
    });
  };
  const isValidCategory = arr => {
    for (const item of arr) {
      const nameLength = item.name.length,
        lengthLower = 2,
        lengthUpper = 200;
      if (nameLength < lengthLower || nameLength > lengthUpper) {
        const itemLang = getDetectionLanguages().find(
          language => language.value === item.lang
        ).label;
        const fieldName = t(
          'p.s.addoreditcategory.category.name',
          'Category Name'
        );
        toast.error(
          <Trans i18nKey="toast.error.name.length2">
            {{fieldName}} in {{itemLang}} must be between {{lengthLower}} and{' '}
            {{lengthUpper}} characters.
          </Trans>
        );
        return false;
      }
    }
    return true;
  };

  const modalFormikConfig = {
    initialValues: getCategoryById(editData),

    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true);

      if (!isLangUnique(values.names)) {
        toast.error(
          <Trans i18nKey="p.s.addoreditcategory.error.default.lang.duplicate">
            Language settings are duplicated.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (!isValidCategory(values.names)) {
        setSubmitting(false);
        return;
      }

      editData.id
        ? await updateCategoryAPI(values)
            .then(res => {
              toast.success(
                <Trans i18nKey="toast.success.update.product">Updated!</Trans>
              );
              setLangOptions(getDetectionLanguages());
              handleCloseModel(false);
            })
            .catch(e => {
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              setSubmitting(false);
            })
        : await addCategoryAPI(values)
            .then(res => {
              toast.success(
                <Trans i18nKey="toast.success.create.product">
                  Created successfully.
                </Trans>
              );
              setLangOptions(getDetectionLanguages());
              handleCloseModel(false);
            })
            .catch(e => {
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              setSubmitting(false);
            });
    },
  };

  return (
    <Modal
      centered
      show={showModal}
      onHide={() => {
        setLangOptions(getDetectionLanguages());
        handleCloseModel(false);
      }}
      size="xl"
    >
      <Modal.Header closeButton={true}>
        <Modal.Title>
          {Object.keys(editData).length === 0 ? (
            <Trans i18nKey="p.s.addoreditcategory.add.category">
              Add Category
            </Trans>
          ) : (
            <Trans i18nKey="p.s.addoreditcategory.edit.category">
              Edit Category
            </Trans>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik {...modalFormikConfig}>
          {formikProps => (
            <Form id={'add-category'}>
              <table
                className="table table-striped table-bordered table-hover dataTable"
                style={{textAlign: 'center'}}
              >
                <thead>
                  <tr>
                    <th>
                      <Trans i18nKey="p.s.addoreditcategory.language">
                        Language
                      </Trans>
                    </th>
                    <th>
                      <Trans i18nKey="p.s.addoreditcategory.default.language">
                        Default Language
                      </Trans>
                    </th>
                    <th>
                      <Trans i18nKey="p.s.addoreditcategory.category.name">
                        Category Name
                      </Trans>
                    </th>
                    <th>
                      <Trans i18nKey="p.s.addoreditcategory.actions">
                        Actions
                      </Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {formikProps.values.names.map((name, index) => (
                    <tr key={`category-language-${index}`}>
                      <td className="col-md-3">
                        <Field
                          name={`names[${index}].lang`}
                          options={langOptions}
                          component={SelectBox}
                        />
                      </td>
                      <td className="col-md-1 text-center">
                        <Field name={`isDefault`}>
                          {({field}) => (
                            <input
                              type="radio"
                              id={`isDefault_${index}`}
                              checked={name.isDefault}
                              onChange={() =>
                                handleDefaultLanguage(formikProps, index)
                              }
                            />
                          )}
                        </Field>
                      </td>
                      <td className="col-md-4">
                        <Field
                          name={`names[${index}].name`}
                          component={InputBox}
                          placeholder={t('c.f.text.max.character', {
                            defaultValue: 'Max. {{maxNum}} characters.',
                            maxNum: 200,
                          })}
                        />
                      </td>
                      <td className="col-md-3">
                        <div className="d-flex justify-content-center">
                          {index + 1 === formikProps.values.names.length && (
                            <ActionButton
                              type={'add'}
                              onClick={() => {
                                onAdd(formikProps);
                              }}
                            />
                          )}
                          {formikProps.values.names.length > 1 && (
                            <ActionButton
                              type={'delete'}
                              onClick={() => onRemove(formikProps, index)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Form>
          )}
        </Formik>
      </Modal.Body>

      <Modal.Footer className="text-right">
        <SimpleButtonGroup
          form={'add-category'}
          isUpdating={true}
          onCancelClick={() => {
            setLangOptions(getDetectionLanguages());
            handleCloseModel(false);
          }}
        />
      </Modal.Footer>
    </Modal>
  );
}
